import React from 'react';
import { Box, Typography } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import type { IWalletFundOptions, IWalletFundPreset } from '../../redux/buzzbridge/types';
import {
    formatApiAmountLabel,
    formatChargeAmount,
    formatUsdWalletCents,
    getPaymentCurrencySymbol,
    isAdminTestPreset,
} from './walletFundOptionsUtils';
import {
    type ICampaignWalletSalesCalc,
    formatWalletCreditLabel,
    grossChargeCentsFromSalesCalc,
    resolveFeeProcessingCentsFromSalesCalc,
} from './walletSalesCalcUtils';

export interface WalletFundingSummaryProps {
    fee: ICampaignWalletSalesCalc;
    /** Local estimate when sales-calc has not resolved wallet credit yet */
    fallbackWalletCreditUsdCents: number;
    fundOptions: IWalletFundOptions | null;
    /** User-clicked preset — wallet credit line uses preset USD, not sales-calc net */
    selectedPreset?: IWalletFundPreset | null;
    activePreset?: IWalletFundPreset | null;
    chargesInLocal: boolean;
    themeStyle: keyof typeof theme;
}

export function WalletFundingSummary({
    fee,
    fallbackWalletCreditUsdCents,
    fundOptions,
    selectedPreset = null,
    activePreset = null,
    chargesInLocal,
    themeStyle,
}: WalletFundingSummaryProps) {
    const t = theme[themeStyle];
    const gross = grossChargeCentsFromSalesCalc(fee);
    const fmt = fee.formatted;
    const paySymbol = fee.symbol ?? getPaymentCurrencySymbol(fundOptions, activePreset ?? selectedPreset);
    const feesWaived =
        fee.fees_waived === true ||
        fee.admin_test_funding === true ||
        isAdminTestPreset(selectedPreset) ||
        isAdminTestPreset(activePreset);
    const feesCents = feesWaived ? 0 : resolveFeeProcessingCentsFromSalesCalc(fee, gross);
    const taxCents = feesWaived ? 0 : typeof fee.tax === 'number' && fee.tax > 0 ? fee.tax : fee.breakdown?.tax ?? 0;
    const fmtPayment = (cents: number) =>
        chargesInLocal ? formatChargeAmount(cents, paySymbol) : formatUsdWalletCents(cents);

    const walletCreditLabel = formatWalletCreditLabel(fee, fallbackWalletCreditUsdCents, fundOptions, selectedPreset);

    const isGrossPayment = fee.resolved_from === 'gross_payment';
    const isWalletCreditPayment =
        fee.resolved_from === 'wallet_credit_payment' || fee.resolved_from === 'payment_currency';

    const localEquiv = formatApiAmountLabel(
        selectedPreset?.wallet_credit_local_equivalent?.formatted ??
            selectedPreset?.conversion?.local_wallet_equivalent_formatted ??
            (fee.breakdown?.wallet_credit_payment_cents ? fmt?.base_amount : undefined),
        fundOptions,
        selectedPreset ?? activePreset,
    );

    return (
        <Box
            sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: t.secondaryBg,
                border: '1px solid ' + t.selectableBorder,
            }}>
            <Typography
                sx={{
                    fontSize: 10,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: t.form_label,
                    mb: 1.5,
                }}>
                Funding summary
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontSize: 13, color: t.form_label }}>Wallet credit (USD)</Typography>
                    <Typography sx={{ fontSize: 13, fontFamily: FONT_ENUM.MAIN_BOLD, color: t.primary_white }}>
                        {walletCreditLabel}
                    </Typography>
                </Box>
                {localEquiv && chargesInLocal && !selectedPreset && isWalletCreditPayment && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: t.form_label }}>Payment amount (local)</Typography>
                        <Typography sx={{ fontSize: 13, color: t.primary_white }}>
                            {formatApiAmountLabel(fmt?.base_amount, fundOptions, activePreset) ?? localEquiv}
                        </Typography>
                    </Box>
                )}
                {localEquiv && chargesInLocal && !selectedPreset && isGrossPayment && fmt?.base_amount && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: t.form_label }}>Wallet credit (local base)</Typography>
                        <Typography sx={{ fontSize: 13, color: t.primary_white }}>
                            {formatApiAmountLabel(fmt.base_amount, fundOptions, activePreset) ?? localEquiv}
                        </Typography>
                    </Box>
                )}
                {localEquiv && chargesInLocal && (selectedPreset || (!isGrossPayment && !isWalletCreditPayment)) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: t.form_label }}>Indicative local equivalent</Typography>
                        <Typography sx={{ fontSize: 13, color: t.primary_white }}>{localEquiv}</Typography>
                    </Box>
                )}
                {feesCents > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: t.form_label }}>Fees and processing</Typography>
                        <Typography sx={{ fontSize: 13, color: t.primary_white }}>
                            {formatApiAmountLabel(fmt?.total_fees, fundOptions, activePreset) ?? fmtPayment(feesCents)}
                        </Typography>
                    </Box>
                )}
                {taxCents > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography sx={{ fontSize: 13, color: t.form_label }}>Tax</Typography>
                        <Typography sx={{ fontSize: 13, color: t.primary_white }}>
                            {formatApiAmountLabel(fmt?.tax, fundOptions, activePreset) ?? fmtPayment(taxCents)}
                        </Typography>
                    </Box>
                )}
                <Box
                    sx={{
                        pt: 1,
                        mt: 0.5,
                        borderTop: '1px solid ' + t.selectableBorder,
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                        alignItems: 'baseline',
                    }}>
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: t.primary_white,
                            flexShrink: 0,
                        }}>
                        You will pay
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 18,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: t.primary_orange,
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                        }}>
                        {formatApiAmountLabel(fmt?.total_amount, fundOptions, activePreset) ??
                            (gross != null ? fmtPayment(gross) : '—')}
                    </Typography>
                </Box>
            </Box>
            <Typography sx={{ fontSize: 11, color: t.form_label, mt: 1.5, lineHeight: 1.45 }}>
                {chargesInLocal
                    ? `Your card is charged in ${
                          fundOptions?.payment_currency_upper ??
                          fundOptions?.payment_currency?.toUpperCase() ??
                          'local currency'
                      }; your campaign wallet is credited in USD after payment succeeds.`
                    : 'Card is charged the total above; your wallet balance increases by the net USD amount after payment succeeds.'}
            </Typography>
        </Box>
    );
}
